if (!customElements.get('mini-split-calculator')) {
  class MiniSplitCalculator extends HTMLElement {
    constructor() {
      super();
      this.currentStep = 0;
      this.totalSteps = 4;
      this.onSubmit = this.onSubmit.bind(this);
      this.onBack = this.onBack.bind(this);
      this.onRestart = this.onRestart.bind(this);
    }

    connectedCallback() {
      this.form = this.querySelector('[data-calculator-form]');
      if (!this.form) return;

      this.stepChip = this.querySelector('[data-step-chip]');
      this.nextButton = this.querySelector('[data-next]');
      this.backButton = this.querySelector('[data-back]');
      this.restartButton = this.querySelector('[data-restart]');
      this.resultValue = this.querySelector('[data-result-value]');
      this.resultCta = this.querySelector('[data-result-cta]');
      this.strings = this.querySelector('.mini-split-calculator-strings');

      this.form.addEventListener('submit', this.onSubmit);
      this.backButton.addEventListener('click', this.onBack);
      this.restartButton.addEventListener('click', this.onRestart);
    }

    disconnectedCallback() {
      if (!this.form) return;
      this.form.removeEventListener('submit', this.onSubmit);
      this.backButton.removeEventListener('click', this.onBack);
      this.restartButton.removeEventListener('click', this.onRestart);
    }

    onSubmit(event) {
      event.preventDefault();

      if (this.currentStep === 0) {
        this.goToStep(1);
      } else if (this.currentStep < this.totalSteps - 1) {
        if (!this.validateStep(this.currentStep)) return;
        this.goToStep(this.currentStep + 1);
      } else if (this.currentStep === this.totalSteps - 1) {
        if (!this.validateStep(this.currentStep)) return;
        this.showResult();
      }
    }

    onBack() {
      if (this.currentStep > 0) {
        this.goToStep(this.currentStep - 1);
      }
    }

    onRestart() {
      this.form.reset();
      this.goToStep(0);
    }

    validateStep(step) {
      const panel = this.querySelector(`[data-step="${step}"]`);
      const fields = panel.querySelectorAll('input, select');
      for (const field of fields) {
        if (!field.reportValidity()) return false;
      }
      return true;
    }

    goToStep(step) {
      this.currentStep = step;

      this.querySelectorAll('[data-step]').forEach((panel) => {
        panel.dataset.active = (Number(panel.dataset.step) === step).toString();
      });

      const isResult = step === this.totalSteps;
      this.backButton.hidden = step <= 1 || isResult;
      this.nextButton.hidden = isResult;
      this.restartButton.hidden = !isResult;

      if (!isResult && this.strings && step > 0) {
        const isLastInput = step === this.totalSteps - 1;
        this.nextButton.textContent = isLastInput
          ? this.strings.dataset.textCalculate
          : this.strings.dataset.textNext;
      }

      if (this.stepChip) {
        this.stepChip.textContent = (this.dataset.textStepLabel || '')
          .replace('[current]', Math.max(step, 1))
          .replace('[total]', this.totalSteps);
      }
    }

    computeBtu() {
      const data = new FormData(this.form);
      const sqft = Number(data.get('sqft')) || 0;
      const ceiling = Number(data.get('ceiling')) || 8;
      const climate = Number(data.get('climate')) || 1;
      const sun = Number(data.get('sun')) || 1;
      const insulation = Number(data.get('insulation')) || 1;
      const occupants = Number(data.get('occupants')) || 2;
      const kitchen = data.get('kitchen') === '1';

      const baseBtu = Number(this.dataset.baseBtu) || 20;

      let btu = sqft * baseBtu;
      btu *= ceiling / 8;
      btu *= climate;
      btu *= sun;
      btu *= insulation;
      if (occupants > 2) btu += (occupants - 2) * 600;
      if (kitchen) btu += 4000;

      return Math.round(btu);
    }

    matchCapacity(btu) {
      const capacities = (this.dataset.capacities || '')
        .split(',')
        .map((value) => Number(value.trim()))
        .filter((value) => value > 0)
        .sort((a, b) => a - b);

      if (!capacities.length) return null;

      // A unit within 5% under the load still covers it per standard sizing practice.
      const match = capacities.find((capacity) => capacity >= btu * 0.95);
      return match || capacities[capacities.length - 1];
    }

    showResult() {
      const btu = this.computeBtu();
      const capacity = this.matchCapacity(btu);
      if (!capacity) return;

      const formatted = capacity.toLocaleString();

      if (this.resultValue) {
        this.resultValue.textContent = (this.dataset.textResultBtu || '[btu]').replace('[btu]', formatted);
      }

      if (this.resultCta) {
        const thousands = Math.round(capacity / 1000);
        this.resultCta.href = (this.dataset.urlTemplate || '#')
          .replace('{btu}', capacity)
          .replace('{btu_k}', thousands);
        this.resultCta.textContent = (this.dataset.textViewMatches || '[btu]').replace('[btu]', formatted);
      }

      this.goToStep(this.totalSteps);
    }
  }

  customElements.define('mini-split-calculator', MiniSplitCalculator);
}
